import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { deleteUserAccount } from "../../store/actions/user.action";

export function DeleteAccountModal({ open, close, userProfile }) {
  const [typedName, setTypedName] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const token = localStorage.getItem("token");

  // Determine the full name the user must type
  const firstName = userProfile?.first_name || userProfile?.firstName || "";
  const lastName = userProfile?.last_name || userProfile?.lastName || "";
  const fullName = `${firstName} ${lastName}`.trim() || userProfile?.email || "";

  useEffect(() => {
    if (open) {
      setTypedName("");
      setConfirmed(false);
      setLoading(false);
    }
  }, [open]);

  if (!open) return null;

  const isNameMatched =
    typedName.trim().length > 0 &&
    typedName.trim().toLowerCase() === fullName.toLowerCase();

  const canDelete = isNameMatched && confirmed && !loading;

  const handleDelete = async (e) => {
    e.preventDefault();
    if (!canDelete) return;

    setLoading(true);
    try {
      await dispatch(
        deleteUserAccount({
          token,
          userId: userProfile?.id,
        })
      );
      close();
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !loading) close();
      }}
    >
      <div className="bg-white rounded-2xl p-6 sm:p-8 w-full max-w-lg shadow-2xl relative animate-fadeIn border border-red-100">
        <button
          type="button"
          onClick={close}
          disabled={loading}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 p-2 rounded-lg text-lg leading-none transition-colors"
          aria-label="Close"
        >
          ✕
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center text-red-600 text-2xl flex-shrink-0">
            ⚠️
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              Delete Your Account
            </h2>
            <p className="text-xs text-gray-500">
              This action cannot be undone
            </p>
          </div>
        </div>

        {/* Warning Banner */}
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-5">
          <p className="text-sm text-red-800 leading-relaxed font-medium">
            Deleting your account will permanently deactivate your profile, revoke your session, remove your tutor courses from public listings, and cancel any upcoming meetings.
          </p>
        </div>

        <form onSubmit={handleDelete}>
          {/* Instructions */}
          <div className="mb-4">
            <label className="block text-sm text-gray-700 font-medium mb-1.5">
              To verify, please type your full name{" "}
              <span className="font-bold text-red-700 select-all bg-red-100 px-2 py-0.5 rounded font-mono">
                {fullName}
              </span>{" "}
              below:
            </label>
            <input
              type="text"
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              placeholder={fullName}
              disabled={loading}
              className={`w-full px-4 py-2.5 border rounded-xl outline-none text-sm transition-all ${
                isNameMatched
                  ? "border-green-500 bg-green-50/30 focus:ring-2 focus:ring-green-400"
                  : "border-gray-300 focus:ring-2 focus:ring-red-400 focus:border-red-400"
              }`}
              autoFocus
            />
            {typedName.trim().length > 0 && !isNameMatched && (
              <p className="text-xs text-red-500 mt-1">
                The name entered does not match "{fullName}".
              </p>
            )}
            {isNameMatched && (
              <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                ✓ Full name matches.
              </p>
            )}
          </div>

          {/* Confirmation Checkbox */}
          <div className="mb-6">
            <label className="flex items-start gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                disabled={loading}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500 cursor-pointer"
              />
              <span className="text-xs text-gray-700 leading-relaxed font-medium">
                I understand that this action is permanent and I confirm that I want to delete my account.
              </span>
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100">
            <button
              type="button"
              onClick={close}
              disabled={loading}
              className="px-5 py-2.5 rounded-xl border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canDelete}
              className="px-6 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold transition-colors shadow-md disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8H4z"
                    ></path>
                  </svg>
                  <span>Deleting...</span>
                </>
              ) : (
                "Delete My Account"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default DeleteAccountModal;
